import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaUsers, FaUserTie, FaChartLine, FaSpinner, FaCog, FaPlus, 
  FaShieldAlt, FaExclamationTriangle, FaCheckCircle, FaFileAlt,
  FaUserPlus, FaBuilding, FaGlobe, FaPhone
} from 'react-icons/fa';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import ContentLibrary from '../components/ContentLibrary';
import ResetPasswordButton from '../components/ResetPasswordButton';
import ClientNomineesView from '../components/advisor/ClientNomineesView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency helper
const formatCurrency = (value) => {
  if (!value && value !== 0) return '₹0';
  const numValue = Math.round(value);
  if (numValue >= 10000000) return `₹${(numValue / 10000000).toFixed(2)}Cr`;
  if (numValue >= 100000) return `₹${(numValue / 100000).toFixed(2)}L`;
  return `₹${numValue.toLocaleString('en-IN')}`;
};

const COLORS = ['#FFD700', '#22C55E', '#3B82F6', '#EF4444', '#A855F7', '#F97316'];

const ManagerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [clientGaps, setClientGaps] = useState([]);
  const [branding, setBranding] = useState(null);
  const [pendingAdvisors, setPendingAdvisors] = useState([]);

  useEffect(() => {
    fetchManagerData();
  }, []);

  const fetchManagerData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, advisorsRes, clientsRes, brandingRes, pendingRes] = await Promise.all([
        axios.get(`${API}/manager/stats`, { headers }),
        axios.get(`${API}/manager/advisors`, { headers }),
        axios.get(`${API}/manager/client-gaps`, { headers }),
        axios.get(`${API}/manager-branding`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/manager/pending-advisors`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setAdvisors(advisorsRes.data || []);
      setClientGaps(clientsRes.data || []);
      setBranding(brandingRes.data);
      setPendingAdvisors(pendingRes.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <FaSpinner className="animate-spin text-[#FFD700]" size={48} />
      </div>
    );
  }

  // Get display name - prioritize user name (which gets updated when saving settings)
  const displayName = user?.name || branding?.advisor_name || 'Manager';
  const displayDesignation = branding?.designation || 'Manager';
  const displayPhone = branding?.phone || user?.phone || '';
  const displayCompany = branding?.company_name || '';

  // Prepare chart data
  const advisorPerformanceData = advisors.map(adv => ({
    name: adv.name?.split(' ')[0] || 'Advisor',
    clients: adv.client_count || 0,
    plans: adv.completed_plans || 0
  }));

  const gapDistributionData = [
    { name: 'Life Insurance Gap', value: clientGaps.filter(c => c.life_gap > 0).length, fill: '#EF4444' },
    { name: 'Health Insurance Gap', value: clientGaps.filter(c => c.health_gap > 0).length, fill: '#F97316' },
    { name: 'Emergency Fund Gap', value: clientGaps.filter(c => c.emergency_gap > 0).length, fill: '#FBBF24' },
    { name: 'No Gaps', value: clientGaps.filter(c => !c.life_gap && !c.health_gap && !c.emergency_gap).length, fill: '#22C55E' }
  ];

  return (
    <div className="space-y-6">
      {/* Manager Header with Branding */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center space-x-4">
            {branding?.company_logo ? (
              <img src={branding.company_logo} alt="Logo" className="w-16 h-16 rounded-lg object-cover" />
            ) : (
              <div className="w-16 h-16 bg-[#FFD700] rounded-lg flex items-center justify-center">
                <FaBuilding className="text-[#1A2942] text-2xl" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">
                Welcome, <span className="text-[#FFD700]">{displayName}!</span>
              </h1>
              <p className="text-[#FFD700] font-medium">{displayDesignation}</p>
              <p className="text-gray-300">
                {displayCompany ? `${displayCompany} | ` : ''}Team Overview & Performance
              </p>
              {displayPhone && (
                <p className="text-gray-400 text-sm mt-1 flex items-center">
                  <FaPhone className="mr-2 text-[#FFD700]" />{displayPhone}
                </p>
              )}
            </div>
          </div>
          
          {/* Website Link */}
          {branding?.website && (
            <div className="mt-4 lg:mt-0 lg:ml-8 lg:text-right bg-[#0F172A]/50 rounded-lg p-4 border border-[#2D5A8F]">
              <p className="text-gray-400 text-xs mb-1">To Learn Personal Finance & Money Management Visit:</p>
              <a 
                href={branding.website.startsWith('http') ? branding.website : `https://${branding.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center lg:justify-end text-[#FFD700] hover:text-white transition-colors font-medium"
              >
                <FaGlobe className="mr-2" />
                {branding.website}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Pending Advisor Requests Alert */}
      {pendingAdvisors.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <FaExclamationTriangle className="text-yellow-400 text-xl" />
            <div>
              <p className="text-yellow-400 font-bold">Pending Advisor Requests</p>
              <p className="text-gray-300 text-sm">
                You have {pendingAdvisors.length} advisor request(s) pending Super Admin approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Advisors */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg" data-testid="total-advisors-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">My Advisors</p>
              <p className="text-4xl font-bold text-white mt-2">{stats?.total_advisors || 0}</p>
            </div>
            <div className="p-4 bg-blue-500 bg-opacity-20 rounded-full">
              <FaUserTie className="text-blue-400" size={32} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
            <p className="text-gray-400 text-xs">Team members under you</p>
          </div>
        </div>

        {/* Total Clients */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg" data-testid="total-clients-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Clients</p>
              <p className="text-4xl font-bold text-white mt-2">{stats?.total_clients || 0}</p>
            </div>
            <div className="p-4 bg-[#FFD700] bg-opacity-20 rounded-full">
              <FaUsers className="text-[#FFD700]" size={32} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
            <p className="text-gray-400 text-xs">Across all advisors</p>
          </div>
        </div>

        {/* Clients with Gaps */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg" data-testid="clients-with-gaps-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Clients with Gaps</p>
              <p className="text-4xl font-bold text-red-400 mt-2">
                {clientGaps.filter(c => c.life_gap > 0 || c.health_gap > 0 || c.emergency_gap > 0).length}
              </p>
            </div>
            <div className="p-4 bg-red-500 bg-opacity-20 rounded-full">
              <FaExclamationTriangle className="text-red-400" size={32} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
            <p className="text-gray-400 text-xs">Need attention</p>
          </div>
        </div>

        {/* Completed Plans */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-lg" data-testid="completed-plans-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Completed Plans</p>
              <p className="text-4xl font-bold text-green-400 mt-2">{stats?.completed_plans || 0}</p>
            </div>
            <div className="p-4 bg-green-500 bg-opacity-20 rounded-full">
              <FaCheckCircle className="text-green-400" size={32} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#2D5A8F]">
            <p className="text-gray-400 text-xs">Financial plans completed</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-xl">
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button 
            onClick={() => navigate('/dashboard/clients/new')}
            className="p-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
            data-testid="add-client-btn"
          >
            <FaPlus />
            <span>Add New Client</span>
          </button>
          <button 
            onClick={() => navigate('/dashboard/team/add-advisor')}
            className="p-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-all flex items-center justify-center space-x-2"
            data-testid="add-advisor-btn"
          >
            <FaUserPlus />
            <span>Add Advisor</span>
          </button>
          <button 
            onClick={() => navigate('/dashboard/team-report')}
            className="p-4 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F] flex items-center justify-center space-x-2"
            data-testid="team-report-btn"
          >
            <FaFileAlt />
            <span>Team Report</span>
          </button>
          <button 
            onClick={() => navigate('/dashboard/settings')}
            className="p-4 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F] flex items-center justify-center space-x-2"
            data-testid="settings-btn"
          >
            <FaCog />
            <span>Branding & Settings</span>
          </button>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Advisor Performance Chart */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-xl">
          <h2 className="text-xl font-bold text-white mb-4">Advisor Performance</h2>
          <div style={{ width: '100%', height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={advisorPerformanceData}>
                <XAxis dataKey="name" tick={{ fill: '#9CA3AF' }} />
                <YAxis tick={{ fill: '#9CA3AF' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F' }}
                  labelStyle={{ color: '#FFD700' }}
                />
                <Legend />
                <Bar dataKey="clients" name="Clients" fill="#FFD700" radius={[4, 4, 0, 0]} />
                <Bar dataKey="plans" name="Plans" fill="#22C55E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gap Distribution Chart */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-xl">
          <h2 className="text-xl font-bold text-white mb-4">Client Gap Distribution</h2>
          <div style={{ width: '100%', height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gapDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={{ stroke: '#9CA3AF' }}
                >
                  {gapDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">My Team</h2>
          <button 
            onClick={() => navigate('/dashboard/team/add-advisor')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all flex items-center space-x-2"
          >
            <FaUserPlus />
            <span>Add Advisor</span>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-[#2D5A8F]">
                <th className="px-4 py-3 text-left">Advisor Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-center">Clients</th>
                <th className="px-4 py-3 text-center">Plans</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {advisors.length > 0 ? advisors.map((advisor, idx) => (
                <tr key={idx} className="border-b border-[#2D5A8F]/50 hover:bg-[#1E3A5F]/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                        <FaUserTie className="text-[#FFD700]" />
                      </div>
                      <span className="text-white font-medium">{advisor.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{advisor.email}</td>
                  <td className="px-4 py-3 text-center text-white font-bold">{advisor.client_count || 0}</td>
                  <td className="px-4 py-3 text-center text-[#FFD700] font-bold">{advisor.completed_plans || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      advisor.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {advisor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ResetPasswordButton
                      userId={advisor.id}
                      userName={advisor.name}
                      userEmail={advisor.email}
                      userRole="advisor"
                      resetEndpoint="manager"
                      onSuccess={() => {}}
                    />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                    No advisors in your team yet. Click "Add Advisor" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Gap Summary */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F] shadow-xl">
        <h2 className="text-xl font-bold text-white mb-4">Client Gap Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-[#2D5A8F]">
                <th className="px-4 py-3 text-left">Client Name</th>
                <th className="px-4 py-3 text-left">Advisor</th>
                <th className="px-4 py-3 text-right">Life Insurance Gap</th>
                <th className="px-4 py-3 text-right">Health Insurance Gap</th>
                <th className="px-4 py-3 text-right">Emergency Fund Gap</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {clientGaps.length > 0 ? clientGaps.map((client, idx) => {
                const hasGap = client.life_gap > 0 || client.health_gap > 0 || client.emergency_gap > 0;
                return (
                  <tr key={idx} className="border-b border-[#2D5A8F]/50 hover:bg-[#1E3A5F]/50">
                    <td className="px-4 py-3 text-white font-medium">{client.name}</td>
                    <td className="px-4 py-3 text-gray-300">{client.advisor_name || '-'}</td>
                    <td className={`px-4 py-3 text-right font-bold ${client.life_gap > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {client.life_gap > 0 ? formatCurrency(client.life_gap) : '✓ Covered'}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${client.health_gap > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                      {client.health_gap > 0 ? formatCurrency(client.health_gap) : '✓ Covered'}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${client.emergency_gap > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {client.emergency_gap > 0 ? formatCurrency(client.emergency_gap) : '✓ Covered'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {hasGap ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                          <FaExclamationTriangle className="inline mr-1" /> Gaps Found
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                          <FaCheckCircle className="inline mr-1" /> All Covered
                        </span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                    No client data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Nominees (Family Vault) */}
      <ClientNomineesView />

      {/* Learning Resources / Content Library */}
      <ContentLibrary title="Paiso Ki Pathshala - Learning Resources" />
    </div>
  );
};

export default ManagerDashboard;
